import { useState } from 'react';
import { Loader2, Plus } from 'lucide-react';

import { AttachDocumentDialog } from '@/components/documents/AttachDocumentDialog';
import { Button } from '@/components/ui/button';
import { useEntityFolder } from '@/hooks/react-query/useDocuments';
import { useLinkFundTransactionDocument } from '@/hooks/react-query/useFundTransactions';
import { DocumentSource } from '@/types/document';

import type { DocumentProof } from './fundTransactionDocumentProof';

interface FundTransactionDocumentAttachButtonProps {
  fundId: string;
  transactionId: string;
  proof: DocumentProof;
}

export function FundTransactionDocumentAttachButton({
  fundId,
  transactionId,
  proof,
}: FundTransactionDocumentAttachButtonProps) {
  const [open, setOpen] = useState(false);
  const folderQuery = useEntityFolder('FUND', fundId);
  const linkDocument = useLinkFundTransactionDocument(fundId, transactionId);
  const folderId = folderQuery.data?.folder_id;

  return (
    <div className="flex items-center gap-2">
      {folderQuery.isError && (
        <span className="text-xs text-destructive">
          Fund folder unavailable
        </span>
      )}
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        disabled={!folderId}
      >
        {folderQuery.isLoading ? (
          <Loader2 className="h-4 w-4 mr-1 animate-spin" />
        ) : (
          <Plus className="h-4 w-4 mr-1" />
        )}
        Attach
      </Button>
      {folderId && (
        <AttachDocumentDialog
          open={open}
          onOpenChange={setOpen}
          entityType="FUND"
          entityId={fundId}
          searchCollectionId={folderId}
          acceptedTags={new Set(proof.acceptedTags)}
          documentSource={DocumentSource.UPLOADED}
          documentTypesHint={proof.documentTypesHint}
          contextOnlyTagHint={proof.contextOnlyTagHint}
          onAttach={async (documentId, documentTypes) => {
            await linkDocument.mutateAsync({
              document_id: documentId,
              document_types: documentTypes,
            });
          }}
        />
      )}
    </div>
  );
}
